import type { DeviceInfo } from 'js/src/components/app/recorder/types';

export const createVideoElement = (elementType: string, instanceId: string) => {
    const id = `${elementType}-${instanceId}`;
    const existingVideo = document.getElementById(id) as HTMLVideoElement | null;
    if (existingVideo) {
        throw new Error(
            `Video element with id '${id}' already exists. This suggests multiple recorder instances of the same type, which could cause conflicts.`,
        );
    }
    const video = document.createElement('video');
    document.body.appendChild(video);
    video.id = id;
    video.srcObject = null;
    video.playsInline = true;
    video.autoplay = true;
    video.muted = true;
    video.volume = 0;
    /**
     * DO NOT REMOVE THIS VIDEO ELEMENT FROM THE DOM OR MAKE IT HIDDEN
     *
     * Testing this in Chrome, the preview would be blank, laggy or missing
     * frames unless the video element is in the viewport and visible.
     * Adding it to the DOM, making it visible but positioning it offscreen
     * does not help either. The following attributes ensure the video is in
     * the viewport, visible to the user and not interactive.
     */
    video.style.position = 'fixed';
    video.style.left = '0';
    video.style.top = '0';
    video.style.width = '1px';
    video.style.height = '1px';
    video.style.pointerEvents = 'none';
    video.style.zIndex = '999999999';
    return video;
};

export const createCanvas = (elementType: string, instanceId: string) => {
    const id = `${elementType}-${instanceId}`;
    const existingCanvas = document.getElementById(id) as HTMLCanvasElement | null;
    if (existingCanvas) {
        throw new Error(
            `Canvas element with id '${id}' already exists. This suggests multiple recorder instances of the same type, which could cause conflicts.`,
        );
    }
    const canvas = document.createElement('canvas');
    canvas.id = id;
    return canvas;
};

export const stopAndRemoveTrackFromStream = (
    stream: MediaStream | null,
    track: MediaStreamTrack,
) => {
    if (stream) {
        stream.removeTrack(track);
        track.stop();
    }
};

export const stopAndRemoveAllTracksFromStream = (stream: MediaStream | null) => {
    if (stream) {
        const tracks = stream.getTracks();
        tracks.forEach((track) => {
            stopAndRemoveTrackFromStream(stream, track);
        });
    }
};

export const stopAndRemoveAllAudioTracksFromStream = (stream: MediaStream | null) => {
    if (stream) {
        const audioTracks = stream.getAudioTracks();
        audioTracks.forEach((track) => {
            stopAndRemoveTrackFromStream(stream, track);
        });
    }
};

export const stopAndRemoveAllVideoTracksFromStream = (stream: MediaStream | null) => {
    if (stream) {
        const videoTracks = stream.getVideoTracks();
        videoTracks.forEach((track) => {
            stopAndRemoveTrackFromStream(stream, track);
        });
    }
};

export const stopAndRemoveAllVideoTracksOtherThanFirstFromStream = (stream: MediaStream | null) => {
    if (stream) {
        const [, ...rest] = stream.getVideoTracks();
        rest.forEach((track) => {
            stopAndRemoveTrackFromStream(stream, track);
        });
    }
};

export const stopAndRemoveAllAudioTracksOtherThanFirstFromStream = (stream: MediaStream | null) => {
    if (stream) {
        const [, ...rest] = stream.getAudioTracks();
        rest.forEach((track) => {
            stopAndRemoveTrackFromStream(stream, track);
        });
    }
};

export const getFirstVideoTrackFromStream = (stream: MediaStream) => {
    const [videoTrack] = stream.getVideoTracks();
    if (!videoTrack) {
        throw new DOMException('No video track found in stream.', 'InvalidStateError');
    }
    return videoTrack;
};

export const getFirstAudioTrackFromStream = (stream: MediaStream): MediaStreamTrack => {
    const [audioTrack] = stream.getAudioTracks();
    if (!audioTrack) {
        throw new DOMException('No audio track found in stream.', 'InvalidStateError');
    }
    return audioTrack;
};

export const getDeviceIdFromTrack = (track: MediaStreamTrack): string | undefined => {
    const settings = track.getSettings();
    return settings.deviceId;
};

/**
 * Scale `width`×`height` down to fit within `maxWidth`×`maxHeight` while
 * preserving aspect ratio. Never scales up. Dimensions are rounded down to even
 * numbers, which some video encoders (e.g. H.264 in Safari's mp4) require.
 */
export const scaleDimensionsToFit = (
    width: number,
    height: number,
    maxWidth: number,
    maxHeight: number,
): { width: number; height: number } => {
    if (width <= 0 || height <= 0) {
        return { width, height };
    }
    const scale = Math.min(1, maxWidth / width, maxHeight / height);
    return {
        width: Math.floor((width * scale) / 2) * 2,
        height: Math.floor((height * scale) / 2) * 2,
    };
};

type AvailableDevicesResult = {
    devices: DeviceInfo[];
    defaultDeviceId: string | undefined;
};
export const getAvailableDevicesForType = async (
    mediaDeviceKind: MediaDeviceKind,
): Promise<AvailableDevicesResult> => {
    let defaultDeviceId: string | undefined;

    const devices = await navigator.mediaDevices.enumerateDevices();
    const filteredDevices = devices.filter(({ kind }) => kind === mediaDeviceKind);

    /**
     * Selectable devices are those that can be selected by the user via some
     * UI element (ex. dropdown).
     *
     * Devices with deviceId 'default' are system default devices that
     * point to the actual default device, but cannot be selected directly. So
     * we filter them out from the selectable devices list.
     */
    const selectableDevices = filteredDevices.filter(({ deviceId }) => deviceId !== 'default');

    /**
     * For devices with deviceId 'default', we need to find the actual device
     * it points to, so that we can set that as the default device to use while
     * setting up media streams.
     *
     * Currently, device with deviceId 'default' is only available for `audioinput`
     * devices. For other device kinds, we will use the first device as the
     * default device.
     */
    const defaultDevice = filteredDevices.find(({ deviceId }) => deviceId === 'default');
    if (defaultDevice) {
        const defaultGroupId = defaultDevice.groupId;
        const actualDefaultDevice = selectableDevices.find(
            ({ groupId }) => groupId === defaultGroupId,
        );
        if (actualDefaultDevice) {
            defaultDeviceId = actualDefaultDevice.deviceId;
        }
    } else {
        if (selectableDevices.length > 0) {
            defaultDeviceId = selectableDevices[0].deviceId;
        }
    }

    const selectableDeviceInfos: DeviceInfo[] = selectableDevices.map(({ deviceId, label }) => ({
        id: deviceId,
        label,
    }));

    return {
        devices: selectableDeviceInfos,
        defaultDeviceId,
    };
};
